import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { lookupLocalCode, emailCodeReport } from "../lib/api";
import {
  ArrowRight, MapPin, Thermometer, ShieldAlert, Mail, Loader2,
  Wind, Flame, Snowflake, ExternalLink, Sparkles, Search,
  CheckCircle2, Lock, Trees, Home, Layers,
} from "lucide-react";
import Footer from "../components/Footer";
import SEO from "../components/SEO";

/**
 * V2.0.12 — Local Code Finder (public lead magnet) — 3-stage funnel.
 *
 *   stage = "input"   — hero + ZIP form, no report yet
 *   stage = "taster"  — 3 unlocked basic blocks + blurred premium with
 *                       email-capture overlay
 *   stage = "unlocked"— full report (post-email)
 *   stage = "convert" — celebratory transition card driving to /auth
 */
const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC",
];

function setMeta(name, content, isOg = false) {
  if (typeof document === "undefined") return;
  const attr = isOg ? "property" : "name";
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export default function LocalCodeFinder() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [zipInput, setZipInput] = useState(searchParams.get("zip") || "");
  const [stateInput, setStateInput] = useState((searchParams.get("state") || "").toUpperCase());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  // V2.0.12 — funnel state machine.
  const [stage, setStage] = useState("input"); // input | taster | unlocked | convert
  const [postEmail, setPostEmail] = useState(null); // { email, county_name }

  // SEO meta — runs once on mount + whenever zip changes.
  useEffect(() => {
    const z = report?.zip_code || zipInput;
    const baseTitle = "Local Building Code Finder · Blueprint AI";
    document.title = z
      ? `Building Codes for ZIP ${z} — Blueprint AI`
      : baseTitle;
    const desc = z
      ? `Residential building code summary for ZIP ${z}: IECC climate zone, state code, energy mandates, typical setbacks, ADU rules. Free advisory tool by Blueprint AI.`
      : "Before you sketch a single floor plan, find out what your county actually allows. Free tool — check setbacks, height limits, and regional residential ordinances by ZIP. By Blueprint AI.";
    setMeta("description", desc);
    setMeta("og:title", baseTitle, true);
    setMeta("og:description", desc, true);
    setMeta("og:type", "website", true);
    setMeta("og:url", `${window.location.origin}/local-code-finder${z ? `?zip=${z}` : ""}`, true);
    setMeta("og:image", `${window.location.origin}/og-code-finder.png`, true);
    setMeta("twitter:card", "summary_large_image");
    return () => {
      document.title = "Blueprint AI";
    };
  }, [report, zipInput]);

  // Auto-run lookup if zip query-param is present.
  useEffect(() => {
    const z = searchParams.get("zip");
    if (z && !report && !loading) {
      runLookup(z, searchParams.get("state") || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function runLookup(zArg, stArg) {
    const z = (zArg ?? zipInput).trim();
    const st = (stArg ?? stateInput).trim().toUpperCase();
    setErr("");
    if (!/^\d{5}$/.test(z)) {
      setErr("Please enter a 5-digit US ZIP code.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await lookupLocalCode(z, st || undefined);
      setReport(data);
      setStage("taster");
      const next = new URLSearchParams();
      next.set("zip", z);
      if (data.state) next.set("state", data.state);
      setSearchParams(next, { replace: false });
      setTimeout(() => {
        document.getElementById("code-finder-report")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Lookup failed. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  }

  function handleEmailUnlocked({ email, county_name, state_name }) {
    setPostEmail({ email, county_name: county_name || state_name || "your area" });
    setStage("convert");
    setTimeout(() => {
      document.getElementById("code-finder-convert")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50">
      <SEO
        title="Local Code Finder — Free Zoning Report"
        description="Free 90-second zoning + climate + hazard report for any U.S. property. Instant report card with the four numbers you need before sketching."
        path="/local-code-finder"
        themeColor="#1c1610"
      />
      <nav className="border-b border-zinc-800 bg-zinc-950/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-7 h-7 bg-brass-400 flex-shrink-0" />
            <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-100 group-hover:text-brass-400 transition-colors">
              Blueprint_AI
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <Link
              to="/"
              data-testid="code-finder-nav-home"
              className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-brass-400 transition-colors"
            >
              ← Home
            </Link>
            {user ? (
              <Link
                to="/dashboard"
                data-testid="code-finder-nav-dashboard"
                className="font-mono text-[10px] uppercase tracking-widest bg-brass-600 text-zinc-950 px-4 py-2 hover:bg-brass-500 transition-colors"
              >
                My Projects
              </Link>
            ) : (
              <Link
                to="/?utm_source=code-finder&utm_medium=nav"
                data-testid="code-finder-nav-signin"
                className="font-mono text-[10px] uppercase tracking-widest bg-brass-600 text-zinc-950 px-4 py-2 hover:bg-brass-500 transition-colors"
              >
                Sign in
              </Link>
            )}
          </div>
        </div>
      </nav>

      <section className="relative overflow-hidden border-b border-zinc-800">
        <div className="absolute inset-0 bp-grid pointer-events-none opacity-40" />
        <div className="absolute -top-32 -right-24 w-[40rem] h-[40rem] rounded-full bg-brass-500/5 blur-3xl pointer-events-none" />

        <div className="relative max-w-4xl mx-auto px-6 py-16 sm:py-20 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 border border-brass-500/40 bg-brass-500/10">
            <Sparkles className="w-3 h-3 text-brass-400" />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-brass-300">
              // Free advisory tool · No signup
            </span>
          </div>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl text-zinc-50 leading-[1.05] tracking-tight">
            Before you sketch a single floor plan,<br />
            <span className="text-brass-400">find out what your county actually allows.</span>
          </h1>
          <p className="mt-6 font-body text-base sm:text-lg text-zinc-400 max-w-2xl mx-auto leading-relaxed">
            Don't waste thousands designing a dream home that violates local zoning laws. Enter your county below to instantly check setbacks, height limits, and regional building ordinances.
          </p>

          <form
            onSubmit={(e) => { e.preventDefault(); runLookup(); }}
            className="mt-8 max-w-xl mx-auto flex flex-col sm:flex-row gap-2"
          >
            <input
              data-testid="code-finder-zip-input"
              type="text"
              inputMode="numeric"
              maxLength={5}
              autoFocus
              placeholder="Enter ZIP code"
              value={zipInput}
              onChange={(e) => setZipInput(e.target.value.replace(/\D/g, "").slice(0, 5))}
              className="flex-1 bg-zinc-900 border border-zinc-700 focus:border-brass-500 outline-none font-mono text-lg px-4 py-3 text-zinc-100 placeholder:text-zinc-600 tracking-widest text-center sm:text-left"
            />
            <select
              data-testid="code-finder-state-select"
              value={stateInput}
              onChange={(e) => setStateInput(e.target.value)}
              className="bg-zinc-900 border border-zinc-700 focus:border-brass-500 outline-none font-mono text-sm px-4 py-3 text-zinc-100"
              aria-label="State (optional, for higher accuracy)"
            >
              <option value="">State (optional)</option>
              {US_STATES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <button
              type="submit"
              data-testid="code-finder-submit-btn"
              disabled={loading || !zipInput}
              className="inline-flex items-center justify-center gap-2 bg-brass-500 hover:bg-brass-400 disabled:opacity-50 disabled:cursor-not-allowed text-zinc-950 font-mono text-xs uppercase tracking-widest px-6 py-3 transition-colors"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              {loading ? "Looking up" : "Look up"}
            </button>
          </form>
          {err && (
            <div data-testid="code-finder-error" className="mt-3 font-mono text-xs text-rose-400">
              {err}
            </div>
          )}
          <div className="mt-4 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
            // Powered by Blueprint AI · IRC 2021 + state amendments · IECC 2021
          </div>
        </div>
      </section>

      {/* Stage 2: Taster — 3 unlocked basics + email-locked premium. */}
      {report && stage === "taster" && (
        <TasterStage report={report} onUnlocked={handleEmailUnlocked} />
      )}

      {/* Stage 3: Unlocked — full report (post-email). */}
      {report && stage === "unlocked" && (
        <UnlockedStage report={report} />
      )}

      {/* Stage 4: Convert — celebratory transition CTA. */}
      {postEmail && stage === "convert" && (
        <ConvertStage postEmail={postEmail} report={report} user={user} onShowFull={() => setStage("unlocked")} />
      )}

      {/* Trust + supporting copy strip — shown when no report yet */}
      {!report && (
        <section
          data-testid="code-finder-why-this-matters"
          className="border-b border-zinc-800"
        >
          <div className="max-w-3xl mx-auto px-6 py-16 sm:py-20">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-brass-400">
              // Why this matters for your build
            </div>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl text-zinc-50 tracking-tight leading-snug">
              A two-foot setback violation can cost you thousands.
            </h2>
            <div className="mt-6 space-y-5 text-zinc-300 text-base sm:text-lg leading-relaxed">
              <p>
                Every municipality enforces strict residential codes. If your layout breaks a front setback rule by even <span className="text-zinc-100 font-medium">two feet</span>, your building permit will be denied — forcing you to pay an architect <span className="text-zinc-100 font-medium">thousands</span> to redraft your files.
              </p>
              <p>
                Blueprint AI bakes your specific local zoning laws directly into the design canvas. Export to <span className="text-zinc-100 font-medium">IFC</span> — the open BIM standard — and send your design to <span className="text-zinc-100 font-medium">any architect, in any software</span>: Revit, ArchiCAD, Vectorworks, BricsCAD, and every other major tool. Your architect picks up where you left off instead of starting from scratch.
              </p>
            </div>

            <div className="mt-10 grid sm:grid-cols-3 gap-3">
              {[
                { icon: Thermometer, label: "IECC climate zone", body: "Insulation R-values + window U-factor required for your zone." },
                { icon: MapPin, label: "State code adoption", body: "IRC 2021, Title 24, Florida Building Code, NYStretch — we know which applies." },
                { icon: ShieldAlert, label: "Hazard overlays", body: "Wildfire (WUI), hurricane, snow load — the high-impact stuff that shapes design." },
              ].map(({ icon: Icon, label, body }) => (
                <div key={label} className="border border-zinc-800 p-5 hover:border-brass-500/40 transition-colors">
                  <Icon className="w-4 h-4 text-brass-400" />
                  <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-brass-300">
                    // {label}
                  </div>
                  <div className="mt-2 text-zinc-400 text-sm leading-relaxed">{body}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <footer className="border-t border-zinc-800 py-8">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
            © 2026 Blueprint AI · v1.0 · Advisory only — verify with your AHJ
          </div>
          <Link
            to="/"
            className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-brass-400 transition-colors"
          >
            ← Back to Blueprint AI
          </Link>
        </div>
      </footer>
      {/* V2.0.50 — Canonical public footer. */}
      <Footer />
    </div>
  );
}


function TasterStage({ report, onUnlocked }) {
  const z = report.zip_code;
  const st = report.state || "—";
  const iecc = report.iecc_climate_zone || "—";
  const ranges = report.typical_ranges || {};
  const taster = ["max_height_ft", "front_setback_ft", "side_setback_ft"];

  return (
    <section
      id="code-finder-report"
      data-testid="code-finder-report"
      className="border-b border-zinc-800"
    >
      <div className="max-w-6xl mx-auto px-6 py-12 sm:py-16">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-brass-400">
              // Free taster · 3 unlocked rules
            </div>
            <h2
              data-testid="code-finder-report-zip"
              className="mt-2 font-display text-3xl sm:text-4xl text-zinc-50 tracking-tight"
            >
              ZIP {z} · {st}
            </h2>
            {report.county_name && (
              <div className="mt-1 text-sm text-zinc-400 font-mono">
                {report.county_name}
              </div>
            )}
          </div>
          <a
            href={report.fema_flood_lookup}
            target="_blank"
            rel="noreferrer"
            data-testid="code-finder-fema-link"
            className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest border border-zinc-700 px-3 py-2 text-zinc-300 hover:border-brass-500 hover:text-brass-300 transition-colors"
          >
            Check FEMA flood map <ExternalLink className="w-3 h-3" />
          </a>
        </div>

        {/* ---- Unlocked taster: IECC zone (compact) + 3 zoning ranges ---- */}
        <div className="mt-8 grid lg:grid-cols-3 gap-4">
          <div
            data-testid="code-finder-card-iecc"
            className="border border-zinc-800 bg-zinc-950 p-6 hover:border-brass-500/40 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Thermometer className="w-4 h-4 text-brass-400" />
              <div className="font-mono text-[10px] uppercase tracking-widest text-brass-300">// IECC zone</div>
            </div>
            <div className="mt-4 text-brass-300 font-display text-4xl leading-none">{iecc}</div>
            <div className="mt-3 text-xs text-zinc-400 leading-relaxed">
              Your climate zone drives insulation R-values + window U-factor required by code.
            </div>
          </div>

          {taster.map((key) => {
            const r = ranges[key];
            if (!r) return null;
            return (
              <div
                key={key}
                data-testid={`code-finder-taster-${key}`}
                className="border border-zinc-800 bg-zinc-950 p-6 hover:border-brass-500/40 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-brass-400" />
                  <div className="font-mono text-[10px] uppercase tracking-widest text-brass-300">
                    // {r.label}
                  </div>
                </div>
                <div className="mt-4 flex items-baseline gap-2">
                  <span className="text-brass-300 font-display text-4xl">{r.typical}</span>
                  <span className="font-mono text-[10px] uppercase text-zinc-500">typical</span>
                </div>
                <div className="mt-2 text-xs text-zinc-400 font-mono">
                  Range: {r.low}–{r.high}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-4 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
          // Above ↑ unlocked · Below ↓ premium — unlock with email
        </div>

        {/* ---- LOCKED: premium blocks under blur + email capture ---- */}
        <LockedSection report={report} onUnlocked={onUnlocked} />
      </div>
    </section>
  );
}


function LockedSection({ report, onUnlocked }) {
  const code = report.state_code || {};
  const mandates = report.energy_mandates || [];
  const hazards = report.hazards || {};
  const adu = report.adu_rules || {};
  const env = report.environmental_protections || [];
  const wind = report.wind_load || {};
  const ranges = report.typical_ranges || {};

  const hazardChips = useMemo(() => {
    const out = [];
    if (hazards.wildland_urban_interface) out.push({ icon: Flame, label: "Wildfire (WUI) likely", color: "amber" });
    if (hazards.hurricane_zone) out.push({ icon: Wind, label: "Hurricane / coastal wind zone", color: "rose" });
    if (hazards.heavy_snow_load) out.push({ icon: Snowflake, label: "Heavy snow load region", color: "sky" });
    return out;
  }, [hazards]);

  return (
    <div className="mt-6 relative">
      {/* Locked content — blurred + non-interactive. The capture card is
          absolutely positioned on top. */}
      <div
        data-testid="code-finder-locked-content"
        className="space-y-4 select-none pointer-events-none"
        style={{ filter: "blur(6px)", opacity: 0.55 }}
        aria-hidden="true"
      >
        <PremiumCard icon={Trees} label="Environmental & watershed protections">
          <ul className="space-y-1.5">
            {env.slice(0, 3).map((m) => (
              <li key={m} className="text-sm text-zinc-200 flex gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 text-emerald-400 flex-shrink-0" /> {m}
              </li>
            ))}
          </ul>
        </PremiumCard>
        <PremiumCard icon={Home} label="Accessory dwelling unit (ADU) rules">
          <div className="text-sm text-zinc-200">
            <div><b>By right:</b> {adu.by_right ? "Yes — statewide" : "Locally controlled"}</div>
            <div className="mt-1"><b>Max units typical:</b> {adu.max_units_typical}</div>
            <div className="mt-1 text-zinc-300 text-xs leading-relaxed">{adu.summary}</div>
          </div>
        </PremiumCard>
        <PremiumCard icon={Wind} label="Wind-load + local material standards">
          <div className="text-sm text-zinc-200">
            <div><b>ASCE 7-22 Vult:</b> {wind.vult_mph}</div>
            <div className="mt-1">
              <b>Impact glazing:</b> {wind.impact_glazing_required ? "Required (HVHZ)" : "Not required"}
            </div>
            {wind.notes && (
              <div className="mt-1 text-zinc-300 text-xs leading-relaxed">{wind.notes}</div>
            )}
          </div>
        </PremiumCard>
        <PremiumCard icon={ShieldAlert} label="Hazard overlays + full zoning ranges">
          <div className="text-sm text-zinc-300">
            {hazardChips.map((h) => h.label).join(" · ")}
            {" — "}
            {Object.values(ranges).length} zoning parameters with detailed ranges.
          </div>
        </PremiumCard>
        <PremiumCard icon={Layers} label="State energy mandates + code identity">
          <div className="text-sm text-zinc-200">
            <div><b>{code.title}</b></div>
            <div className="mt-1 text-xs text-zinc-300">{code.energy_code}</div>
            <ul className="mt-2 space-y-1">
              {mandates.slice(0, 3).map((m) => (
                <li key={m} className="text-xs text-zinc-300">• {m}</li>
              ))}
            </ul>
          </div>
        </PremiumCard>
      </div>

      {/* Absolute capture card — the conversion moment. */}
      <div className="absolute inset-0 flex items-center justify-center px-4">
        <LeadCaptureCard report={report} onUnlocked={onUnlocked} />
      </div>
    </div>
  );
}


function PremiumCard({ icon: Icon, label, children }) {
  return (
    <div className="border border-zinc-800 bg-zinc-950 p-5">
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-brass-400" />
        <div className="font-mono text-[10px] uppercase tracking-widest text-brass-300">// {label}</div>
      </div>
      <div className="mt-3">{children}</div>
    </div>
  );
}


function LeadCaptureCard({ report, onUnlocked }) {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");

  async function submit(e) {
    e.preventDefault();
    setErr("");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErr("Please enter a valid email address.");
      return;
    }
    setSending(true);
    try {
      const { data } = await emailCodeReport({
        email,
        zip: report.zip_code,
        state: report.state,
        source: "code-finder-locked",
      });
      onUnlocked({
        email,
        county_name: data?.county_name || report.county_name,
        state_name: data?.state_name || report.state_name,
      });
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Could not send right now. Try again later.");
    } finally {
      setSending(false);
    }
  }

  return (
    <form
      onSubmit={submit}
      data-testid="code-finder-lock-card"
      className="relative w-full max-w-lg bg-zinc-950 border-2 border-brass-500/60 shadow-[0_24px_64px_rgba(0,0,0,0.6)] p-7"
    >
      <div className="flex items-center gap-2">
        <Lock className="w-4 h-4 text-brass-400" />
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-brass-300">
          // Free PDF · One-page local-code summary
        </div>
      </div>
      <h3 className="mt-4 font-display text-xl sm:text-2xl text-zinc-50 leading-snug tracking-tight">
        Get a one-page PDF of your local code rules for ZIP {report.zip_code}.<br />
        <span className="text-brass-300">Drop your email — we'll send it.</span>
      </h3>
      <ul className="mt-4 space-y-2">
        {[
          "One-page PDF of your full localized code profile — keep, print, share with your contractor",
          "Environmental & watershed protections",
          "Accessory Dwelling Unit (ADU) statewide rules",
          "ASCE 7-22 wind-load + impact-glazing standards",
        ].map((b) => (
          <li key={b} className="flex items-start gap-2 text-sm text-zinc-200">
            <CheckCircle2 className="w-4 h-4 mt-0.5 text-brass-400 flex-shrink-0" />
            <span>{b}</span>
          </li>
        ))}
      </ul>
      <div className="mt-5 flex flex-col sm:flex-row gap-2">
        <input
          data-testid="code-finder-lock-email-input"
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@email.com"
          className="flex-1 bg-zinc-900 border border-zinc-700 focus:border-brass-500 outline-none font-mono text-sm px-3 py-3 text-zinc-100 placeholder:text-zinc-600"
        />
        <button
          type="submit"
          data-testid="code-finder-lock-submit"
          disabled={sending}
          className="inline-flex items-center justify-center gap-2 bg-brass-500 hover:bg-brass-400 disabled:opacity-50 text-zinc-950 font-mono text-[11px] uppercase tracking-widest px-5 py-3 transition-colors"
        >
          {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
          {sending ? "Sending" : "Email me the PDF"}
        </button>
      </div>
      {err && (
        <div data-testid="code-finder-lock-error" className="mt-2 font-mono text-xs text-rose-400">{err}</div>
      )}
      <div className="mt-3 font-mono text-[9px] uppercase tracking-widest text-zinc-500">
        // No spam · 1 email · advisory only · unsubscribe anytime
      </div>
    </form>
  );
}


function UnlockedStage({ report }) {
  const z = report.zip_code;
  const st = report.state || "—";
  const code = report.state_code || {};
  const mandates = report.energy_mandates || [];
  const hazards = report.hazards || {};
  const adu = report.adu_rules || {};
  const env = report.environmental_protections || [];
  const wind = report.wind_load || {};
  const ranges = report.typical_ranges || {};

  const hazardChips = useMemo(() => {
    const out = [];
    if (hazards.wildland_urban_interface) out.push({ icon: Flame, label: "Wildfire (WUI) likely", color: "amber" });
    if (hazards.hurricane_zone) out.push({ icon: Wind, label: "Hurricane / coastal wind zone", color: "rose" });
    if (hazards.heavy_snow_load) out.push({ icon: Snowflake, label: "Heavy snow load region", color: "sky" });
    return out;
  }, [hazards]);

  return (
    <section
      id="code-finder-unlocked"
      data-testid="code-finder-unlocked"
      className="border-b border-zinc-800"
    >
      <div className="max-w-6xl mx-auto px-6 py-12 sm:py-16 space-y-6">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-400">
            // Full report · Unlocked
          </div>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl text-zinc-50 tracking-tight">
            ZIP {z} · {st}
          </h2>
          {report.county_name && (
            <div className="mt-1 text-sm text-zinc-400 font-mono">{report.county_name}</div>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-4">
          <PremiumCard icon={Thermometer} label="IECC zone">
            <div className="text-brass-300 font-display text-3xl">{report.iecc_climate_zone}</div>
            {report.iecc_insulation && (
              <div className="mt-3 text-xs text-zinc-300 space-y-0.5 font-mono">
                <div>Ceiling R-{report.iecc_insulation.roof}</div>
                <div>Wall R-{report.iecc_insulation.wall}</div>
                <div>Floor R-{report.iecc_insulation.floor}</div>
                <div>Window U {report.iecc_insulation.window_u}</div>
              </div>
            )}
          </PremiumCard>
          <PremiumCard icon={MapPin} label="State code">
            <div className="text-zinc-100 text-sm font-medium">{code.title}</div>
            <div className="mt-1 text-xs text-zinc-400 leading-relaxed">{code.base}</div>
            <div className="mt-3 font-mono text-[10px] uppercase tracking-widest text-brass-300">// energy</div>
            <div className="text-xs text-zinc-300">{code.energy_code}</div>
          </PremiumCard>
          <PremiumCard icon={ShieldAlert} label="Hazards">
            {hazardChips.length === 0 ? (
              <div className="text-zinc-400 text-sm">No major overlays for your state.</div>
            ) : (
              <div className="space-y-2">
                {hazardChips.map(({ icon: Icon, label, color }) => (
                  <div key={label} className={`flex items-start gap-2 text-sm text-${color}-200`}>
                    <Icon className={`w-4 h-4 mt-0.5 text-${color}-400 flex-shrink-0`} />
                    <span>{label}</span>
                  </div>
                ))}
              </div>
            )}
          </PremiumCard>
        </div>

        {mandates.length > 0 && (
          <PremiumCard icon={Layers} label="State energy mandates">
            <ul className="space-y-1.5">
              {mandates.map((m) => (
                <li key={m} className="flex items-start gap-2 text-sm text-zinc-200">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 text-brass-400 flex-shrink-0" /> {m}
                </li>
              ))}
            </ul>
          </PremiumCard>
        )}

        <PremiumCard icon={Home} label="Accessory dwelling units (ADU)">
          <div className="text-sm text-zinc-200 space-y-1">
            <div><b>By right:</b> {adu.by_right ? "Yes — statewide" : "Locally controlled"}</div>
            <div><b>Max units typical:</b> {adu.max_units_typical}</div>
            <div className="mt-1 text-zinc-300 text-xs leading-relaxed">{adu.summary}</div>
          </div>
        </PremiumCard>

        <PremiumCard icon={Trees} label="Environmental & watershed protections">
          <ul className="space-y-1.5">
            {env.map((e) => (
              <li key={e} className="flex items-start gap-2 text-sm text-zinc-200">
                <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 text-emerald-400 flex-shrink-0" /> {e}
              </li>
            ))}
          </ul>
        </PremiumCard>

        <PremiumCard icon={Wind} label="Wind-load + coastal standards">
          <div className="text-sm text-zinc-200 space-y-1">
            <div><b>ASCE 7-22 Vult:</b> {wind.vult_mph}</div>
            <div><b>Impact glazing:</b> {wind.impact_glazing_required ? "Required (HVHZ)" : "Not required"}</div>
            {wind.notes && <div className="text-zinc-300 text-xs leading-relaxed">{wind.notes}</div>}
          </div>
        </PremiumCard>

        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-brass-400">
            // Typical residential zoning ranges (full set)
          </div>
          <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {Object.entries(ranges).map(([key, r]) => (
              <div
                key={key}
                data-testid={`code-finder-range-${key}`}
                className="border border-zinc-800 p-4 hover:border-brass-500/40 transition-colors"
              >
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">{r.label}</div>
                <div className="mt-2 flex items-baseline gap-2">
                  <span className="text-brass-300 font-display text-2xl">{r.typical}</span>
                  <span className="font-mono text-[10px] uppercase text-zinc-500">typical</span>
                </div>
                <div className="mt-1 text-xs text-zinc-400 font-mono">{r.low}–{r.high}</div>
              </div>
            ))}
          </div>
        </div>

        <div
          data-testid="code-finder-advisory"
          className="border border-amber-500/40 bg-amber-500/10 p-4 flex items-start gap-3"
        >
          <ShieldAlert className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-amber-300">// Advisory only</div>
            <div className="mt-1 text-sm text-amber-100/90 leading-relaxed">
              These ranges reflect typical US residential patterns. Always verify with your local AHJ before construction. Last reviewed: {report.last_reviewed_at}.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}


function ConvertStage({ postEmail, report, user, onShowFull }) {
  const county = postEmail.county_name;
  // V2.0.37 — Soft pricing pitch replaces the celebratory CTA. Visitors
  // who provided their email get to see the actual architect-package
  // pricing they'd pay to ship a real IFC for their build — keeps the
  // momentum hot while the PDF is en route.
  const tiers = [
    {
      id: "smart_start",
      label: "Smart Start",
      sqft: "≤ 1,500 sqft",
      blurb: "Tiny homes, ADUs, garages, single-story starters.",
      price: 99,
    },
    {
      id: "residential_standard",
      label: "Residential Standard",
      sqft: "≤ 3,000 sqft",
      blurb: "The 2-4 bedroom American suburban sweet spot.",
      price: 179,
      featured: true,
    },
    {
      id: "custom_premier",
      label: "Custom Premier",
      sqft: "≤ 5,000 sqft +",
      blurb: "Large custom homes. +$100 per 1,000 sqft above 5,000.",
      price: 299,
    },
  ];
  const ctaHref = user
    ? `/dashboard?utm_source=code-finder-pdf&utm_medium=convert&utm_campaign=architect-pitch&zip=${report.zip_code}`
    : `/?utm_source=code-finder-pdf&utm_medium=convert&utm_campaign=architect-pitch&zip=${report.zip_code}`;
  return (
    <section
      id="code-finder-convert"
      data-testid="code-finder-convert"
      className="border-b border-zinc-800"
    >
      <div className="max-w-5xl mx-auto px-6 py-16 sm:py-24">
        {/* PDF-delivered receipt strip */}
        <div className="flex items-center justify-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 border border-emerald-500/50 bg-emerald-500/10">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-300">
              // Zoning PDF on its way to {postEmail.email}
            </span>
          </div>
        </div>

        {/* Pivot copy — soft pricing pitch */}
        <div className="text-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-brass-400">
            // While your PDF emails — the next step
          </div>
          <h2
            data-testid="code-finder-convert-headline"
            className="mt-3 font-display text-3xl sm:text-5xl text-zinc-50 leading-[1.1] tracking-tight"
          >
            Take {county || "your zoning data"} from advisory<br />
            to a <span className="text-brass-400">build-ready IFC.</span>
          </h2>
          <p className="mt-5 font-body text-base sm:text-lg text-zinc-400 max-w-2xl mx-auto leading-relaxed">
            A human draftsman would charge <span className="text-zinc-100 font-medium">$1,500–$3,000</span> to
            turn your zoning rules into IFC + PDF blueprints. Blueprint AI ships
            the same deliverable, scoped to your home size — see what unlocking
            the full Architect Package costs:
          </p>
        </div>

        {/* Tier pricing grid */}
        <div className="mt-10 grid sm:grid-cols-3 gap-3" data-testid="code-finder-pricing-grid">
          {tiers.map((t) => (
            <div
              key={t.id}
              data-testid={`code-finder-tier-${t.id}`}
              className={
                "border p-5 transition-colors flex flex-col " +
                (t.featured
                  ? "border-brass-500 bg-brass-950/30 shadow-[0_0_40px_-20px_rgba(196,169,107,0.4)]"
                  : "border-zinc-800 hover:border-brass-500/40 bg-zinc-950")
              }
            >
              <div className="font-mono text-[10px] uppercase tracking-widest text-brass-300">
                // {t.label}
              </div>
              <div className="mt-2 font-mono text-xs text-zinc-400">{t.sqft}</div>
              <div className="mt-4 font-display text-3xl text-zinc-50">
                ${t.price}
                {t.id === "custom_premier" && <span className="text-base text-zinc-500">+</span>}
              </div>
              <p className="mt-2 text-zinc-400 text-sm leading-relaxed flex-1">{t.blurb}</p>
              <div className="mt-4 pt-3 border-t border-zinc-800 space-y-1.5">
                {[
                  "IFC 4 BIM file (Revit / ArchiCAD / Vectorworks)",
                  "Architectural PDF + dimensions",
                  "Code-compliance audit log",
                ].map((b) => (
                  <div key={b} className="flex items-start gap-1.5 text-[11px] text-zinc-300">
                    <CheckCircle2 className="w-3 h-3 mt-0.5 text-brass-400 flex-shrink-0" />
                    <span>{b}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom dual-CTA */}
        <div className="mt-10 flex flex-col items-center gap-4">
          <Link
            to={ctaHref}
            data-testid="code-finder-convert-cta"
            className="inline-flex items-center justify-center gap-3 bg-brass-500 hover:bg-brass-400 text-zinc-950 font-mono text-sm uppercase tracking-widest px-8 py-5 transition-colors"
          >
            Start free · design first, pay only at export <ArrowRight className="w-5 h-5" />
          </Link>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 max-w-xl text-center leading-relaxed">
            Free to design, edit, view in 3D + scan local codes. You only pay
            when you export the build-ready IFC + PDF for your architect.
          </div>
          <button
            type="button"
            data-testid="code-finder-show-full-btn"
            onClick={onShowFull}
            className="mt-2 font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-brass-300 transition-colors"
          >
            Or — view the full local-code report on this page →
          </button>
        </div>
      </div>
    </section>
  );
}
